<script setup>
const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  totalItems: {
    type: Number,
    default: 0
  },
  perPage: {
    type: Number,
    default: 1
  },
  disabled: {
    type: Boolean,
    default: false
  },
})

const { currentPage, totalItems, perPage, disabled } = toRefs(props)

const emit = defineEmits(['pageChange'])
const { isMobileView, isBreakpointView } = useScreen(450)
const { isBreakpointView: isXSBreakpointView } = useScreen(350)

const totalPages = computed(() => Math.ceil(totalItems.value / perPage.value))

const getButtons = (buttonCount) => {
  const buttons = []
  const halfButtonCount = Math.ceil(buttonCount / 2)
  let startPage = Math.max(1, currentPage.value - halfButtonCount)
  let endPage = Math.min(totalPages.value, currentPage.value + halfButtonCount)

  if (currentPage.value - halfButtonCount < 1) {
    endPage = Math.min(totalPages.value, endPage + (halfButtonCount - currentPage.value + 1))
  }

  if (currentPage.value + halfButtonCount > totalPages.value) {
    startPage = Math.max(1, startPage - (currentPage.value + halfButtonCount - totalPages.value))
  }

  if (startPage > 1) {
    buttons.push(1)
    if (startPage > 2) {
      buttons.push('...')
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    buttons.push(i)
  }

  if (endPage < totalPages.value) {
    if (endPage < totalPages.value - 1) {
      buttons.push('...')
    }
    buttons.push(totalPages.value)
  }

  return buttons
}

const baseButtonClasses = "max-h-[40px] border hover:bg-gray-300/10 active:bg-gray-300/30 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg"
const buttons = computed(() => !isMobileView.value ? getButtons(4) : !isBreakpointView.value ? getButtons(3) : !isXSBreakpointView.value ? getButtons(1) : getButtons(0))
</script>

<template>
  <div class="flex gap-1.5 font-normal text-center items-center justify-center text-sm *:transition">
    <button
      :disabled="disabled || currentPage <= 1"
      class="px-1 lg:px-8 h-[40px]"
      :class="baseButtonClasses"
      @click="emit('pageChange', currentPage - 1)"
    >
      <IconArrowLeft class="text-darkBlue size-7" />
    </button>
    <template v-for="(value, index) in buttons" :key="index">
      <button
        v-if="value === '...'"
        class="px-4 py-2.5 disabled:cursor-not-allowed disabled:opacity-50 hidden sm:block"
        disabled
      >
        {{ value }}
      </button>
      <button
        v-else
        :disabled="disabled || currentPage === value"
        class="px-4 py-2.5"
        :class="[currentPage === value ? 'max-h-[40px] bg-gray-500/20 rounded-lg' : baseButtonClasses]"
        @click="emit('pageChange', value)"
      >
        {{ value }}
      </button>
    </template>
    <button
      :disabled="disabled || currentPage >= totalPages"
      class="px-1 lg:px-8 h-[40px]"
      :class="baseButtonClasses"
      @click="emit('pageChange', currentPage + 1)"
    >
      <IconArrowRight class="text-darkBlue size-7" />
    </button>
  </div>
</template>